<template>
    <div class="color-picker">
        <VDropdown v-if="type === 'chrome'" offset="0" class="color-picker__content">
            <slot>
                <input
                    class="color-picker__input"
                    type="color"
                    :value="inputValue"
                    @click.prevent
                >
            </slot>

            <template #popper>
                <aside class="color-popup" @click.stop>
                    <chrome-picker
                        class="color-popup__picker"
                        :value="inputValue"
                        tabindex="0"
                        @input="onInput"
                    />
                </aside>
            </template>
        </VDropdown>

        <VDropdown v-else offset="0" class="color-picker__content">
            <slot>
                <input
                    class="color-picker__input"
                    type="color"
                    :value="inputValue"
                    @click.prevent
                >
            </slot>

            <template #popper>
                <aside class="color-popup" @click.stop>
                    <header class="color-popup__header">
                        <button
                            class="color-popup__button"
                            :class="{ [STATE_CLASSES.SELECTED]: visiblePaneIndex === 0 }"
                            @click="visiblePaneIndex = 0"
                        >
                            Brand
                        </button>

                        <button
                            class="color-popup__button"
                            :class="{ [STATE_CLASSES.SELECTED]: visiblePaneIndex === 1 }"
                            @click="visiblePaneIndex = 1"
                        >
                            Utility
                        </button>
                    </header>

                    <div class="color-popup__panes">
                        <div class="color-popup__panes-content" :style="panesContentStyle">
                            <section class="color-popup__pane">
                                <compact-picker
                                    class="color-popup__picker"
                                    :value="pickerValue"
                                    :palette="brandColors"
                                    :flex-color-settings="brandColorObjects"
                                    :list-layout="true"
                                    :show-no-color="true"
                                    :show-transparent="true"
                                    tabindex="0"
                                    @input="onInput"
                                />
                            </section>

                            <section class="color-popup__pane">
                                <compact-picker
                                    class="color-popup__picker"
                                    :value="pickerValue"
                                    :palette="utilityColors"
                                    :flex-color-settings="utilityColorObjects"
                                    :list-layout="true"
                                    tabindex="0"
                                    @input="onInput"
                                />
                            </section>
                        </div>
                    </div>
                </aside>
            </template>
        </VDropdown>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import { Chrome, Compact } from '@xcentium/vue-color';
import { mixin as clickaway } from 'vue-clickaway';

import { STATE_CLASSES } from '@/data/constants/classes';
import colorData from '@/data/constants/colors';

export default {
    name: 'color-picker',
    components: {
        'chrome-picker': Chrome,
        'compact-picker': Compact,
    },
    mixins: [clickaway],

    props: {
        type: {
            type: String,
            default: 'compact',
        },

        value: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            inputValue: this.value,
            pickerValue: this.value,
            transparentValue: '#FFFFFF00', // value to select vue-color Transparent swatch
            noColorValue: '#00000000', // value to select vue-color No Color swatch
            noColorInputValue: '#FFFFFF',
            transparentFlag: 'transparent',
            brandCategory: 'brand',
            utilityCategory: 'utility',
            STATE_CLASSES,
            visiblePaneIndex: 0,
        };
    },

    computed: {
        ...mapState({
            settings: state => state.settings,
        }),

        brandColorObjects() {
            return this.filterColorCategory(this.settings.colors, this.brandCategory).filter(c => c.active === true);
        },

        utilityColorObjects() {
            return this.filterColorCategory(this.settings.colors, this.utilityCategory).filter(c => c.active === true);
        },

        brandColors() {
            return this.brandColorObjects.map(c => c.value.toLowerCase());
        },

        utilityColors() {
            return this.utilityColorObjects.map(c => c.value.toLowerCase());
        },

        panesContentStyle() {
            const offset = this.visiblePaneIndex * 100;

            return { transform: `translateX(-${ offset }%)` };
        },
    },

    watch: {
        value: {
            handler(newVal) {
                this.setInternalValues(newVal);
            },

            immediate: true,
        },
    },

    methods: {
        onInput(color) {
            let colorValue;

            if (color.nocolor) {
                colorValue = colorData.NO_COLOR_FLAG;
            } else {
                colorValue = (color.rgba.a === 0 && color.hex8 !== this.noColorValue) ? this.transparentFlag : color.hex;
            }

            this.setInternalValues(colorValue);
            this.$emit('input', colorValue);
        },

        filterColorCategory(settingsColors, categoryName) {
            return settingsColors.filter(c => c.category === categoryName);
        },

        setInternalValues(newVal) {
            if (newVal === this.transparentFlag) {
                this.inputValue = this.noColorInputValue;
                this.pickerValue = this.transparentValue;
            } else if (newVal === colorData.NO_COLOR_FLAG) {
                this.inputValue = this.noColorInputValue;
            } else {
                this.inputValue = newVal;
                this.pickerValue = newVal;
            }
        },
    },
};
</script>

<style lang="scss">
.color-picker {
    display: inline-block;

    &__content {
        display: inline-block;
    }

    &__input {
        background-color: transparent;
        padding: 0;
    }
}

.color-popup {
    &__header {
        display: flex;
        justify-content: space-around;
        border-bottom: 1px solid $color-picker-border-color;
        margin-bottom: 16px;
    }

    &__button {
        padding: 0px 15px 9px;
        font-size: 14px;
        color: $color-picker-text-color;

        &.#{ $is-selected } {
            color: $color-picker-selected-color;
            font-weight: 700;
        }
    }

    &__panes {
        width: 240px;
        overflow: hidden;
    }

    &__panes-content {
        display: flex;
        position: relative;
        transition: transform 0.45s;
    }

    &__picker {
        box-shadow: none !important;
        padding: 0 !important;
    }
}
</style>
